import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Heart, Hand, Sparkles, Users, MapPin, Check, Settings, Loader2, Camera, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Matchmaking = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [waves, setWaves] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [formData, setFormData] = useState({
    is_active: false,
    looking_for: 'everyone',
    bio: '',
    interests: []
  });

  const interestOptions = [
    'Dancing', 'Live Music', 'Cocktails', 'Beer', 'Wine',
    'Sports', 'Karaoke', 'Pool', 'Darts', 'Conversation',
    'Nightlife', 'Chill Vibes', 'Party', 'Dating', 'Friends'
  ];

  useEffect(() => {
    fetchData();
    // Refresh user data when component mounts
    if (refreshUser) refreshUser();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, wavesRes] = await Promise.all([
        axios.get(`${API}/matchmaking/profile`),
        axios.get(`${API}/matchmaking/my-waves`)
      ]);
      setProfile(profileRes.data);
      setWaves(wavesRes.data);
      setFormData({
        is_active: profileRes.data.is_active || false,
        looking_for: profileRes.data.looking_for || 'everyone',
        bio: profileRes.data.bio || '',
        interests: profileRes.data.interests || []
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to load matchmaking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/matchmaking/profile`, formData);
      toast.success(formData.is_active ? 'Single & Mingling mode activated! 💘' : 'Matchmaking profile updated');
      setIsSettingsOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  // Get user profile picture URL
  const getProfilePicUrl = () => {
    if (user?.profile_picture_url) {
      return user.profile_picture_url.startsWith('/api') 
        ? `${BACKEND_URL}${user.profile_picture_url}` 
        : user.profile_picture_url;
    }
    return null;
  };

  // Handle photo upload for dating profile
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);

      await axios.post(`${API}/auth/upload-profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Refresh user data to get new profile picture
      await refreshUser();
      toast.success('Profile photo updated!');
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pink-900/20 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Heart className="w-6 h-6 text-pink-400" />
              Single & Mingling
            </h1>
          </div>
          <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <Settings className="w-5 h-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border border-slate-700 max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" />
                  Matchmaking Settings
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Profile Photo Upload */}
                <div className="flex flex-col items-center">
                  <p className="text-sm text-slate-300 mb-3">Your Dating Photo</p>
                  <div 
                    className="relative w-36 h-36 cursor-pointer group"
                    onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
                  >
                    <div className={`w-full h-full rounded-full border-4 ${
                      getProfilePicUrl() 
                        ? 'border-pink-500' 
                        : 'border-dashed border-pink-500/50 hover:border-pink-400'
                    } transition-all flex items-center justify-center overflow-hidden bg-pink-600/20 shadow-lg shadow-pink-500/20`}>
                      {uploadingPhoto ? (
                        <Loader2 className="w-10 h-10 text-pink-400 animate-spin" />
                      ) : getProfilePicUrl() ? (
                        <img src={getProfilePicUrl()} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Camera className="w-12 h-12 text-pink-400 mx-auto" />
                          <span className="text-sm text-pink-400">Add Photo</span>
                        </div>
                      )}
                    </div>
                    {getProfilePicUrl() && !uploadingPhoto && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="w-10 h-10 text-white" />
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {getProfilePicUrl() ? 'Click to change photo' : 'Add a photo to attract matches!'}
                  </p>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Single & Mingling Mode</p>
                    <p className="text-sm text-slate-400">Show yourself to other singles</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>

                {/* Looking For */}
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">I&apos;m interested in</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['men', 'women', 'everyone'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData({ ...formData, looking_for: option })}
                        className={`p-2 rounded-lg text-sm capitalize transition-all ${
                          formData.looking_for === option
                            ? 'bg-pink-600 text-white'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">About Me</label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell others a bit about yourself..."
                    className="bg-slate-800 border-slate-700 text-white"
                    rows={3}
                  />
                </div>

                {/* Interests */}
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Interests</label>
                  <div className="flex flex-wrap gap-2">
                    {interestOptions.map((interest) => (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => toggleInterest(interest)}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          formData.interests.includes(interest)
                            ? 'bg-pink-600 text-white'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  className="w-full bg-pink-600 hover:bg-pink-700"
                  disabled={saving}
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Save Settings</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Status Card */}
        <Card className={`glass p-6 border mb-6 fade-in ${
          profile?.is_active ? 'border-pink-500/50' : 'border-slate-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Big User Profile Picture with Upload Option */}
              <div 
                className="relative cursor-pointer group"
                onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
              >
                {uploadingPhoto ? (
                  <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
                  </div>
                ) : getProfilePicUrl() ? (
                  <>
                    <img 
                      src={getProfilePicUrl()}
                      alt={user?.username || 'User'}
                      className={`w-24 h-24 rounded-full object-cover border-4 shadow-lg ${
                        profile?.is_active ? 'border-pink-500 shadow-pink-500/30' : 'border-slate-600'
                      }`}
                    />
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  </>
                ) : (
                  <div className={`w-24 h-24 rounded-full flex flex-col items-center justify-center border-4 border-dashed ${
                    profile?.is_active 
                      ? 'bg-gradient-to-br from-pink-500/20 to-rose-500/20 border-pink-500/50 hover:border-pink-400' 
                      : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                  }`}>
                    <Camera className="w-8 h-8 text-pink-400" />
                    <span className="text-xs text-pink-400 mt-1">Add Photo</span>
                  </div>
                )}
                {profile?.is_active && getProfilePicUrl() && (
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-pink-500 rounded-full flex items-center justify-center border-2 border-slate-900">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {profile?.is_active ? 'You\'re Single & Mingling!' : 'Matchmaking Off'}
                </h2>
                <p className="text-slate-400">
                  {!getProfilePicUrl() 
                    ? 'Add a photo to get started!'
                    : profile?.is_active 
                      ? 'Other singles can see you at bars' 
                      : 'Turn on to meet other singles'}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setIsSettingsOpen(true)}
              className={profile?.is_active ? 'bg-pink-600 hover:bg-pink-700' : 'bg-slate-700 hover:bg-slate-600'}
            >
              {profile?.is_active ? 'Edit Profile' : 'Enable'}
            </Button>
          </div>
        </Card>

        {/* Waves Stats */}
        {waves && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="glass p-4 border border-slate-700 text-center fade-in">
              <Hand className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{waves.sent_count}</div>
              <div className="text-sm text-slate-400">Waves Sent</div>
            </Card>
            <Card className="glass p-4 border border-slate-700 text-center fade-in">
              <Hand className="w-8 h-8 text-purple-400 mx-auto mb-2 scale-x-[-1]" />
              <div className="text-2xl font-bold text-white">{waves.received_count}</div>
              <div className="text-sm text-slate-400">Waves Received</div>
            </Card>
            <Card className="glass p-4 border border-pink-500/50 text-center fade-in">
              <Heart className="w-8 h-8 text-pink-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-pink-400">{waves.matches_count}</div>
              <div className="text-sm text-slate-400">Matches!</div>
            </Card>
          </div>
        )}

        {/* How It Works */}
        <Card className="glass p-6 border border-slate-700 fade-in">
          <h3 className="text-lg font-semibold text-white mb-4">How It Works</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-pink-400 font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-white">Enable Single & Mingling</p>
                <p className="text-sm text-slate-400">Turn on matchmaking in settings</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-pink-400 font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-white">Check In at a Bar</p>
                <p className="text-sm text-slate-400">Your profile becomes visible to other singles there</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-pink-400 font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-white">Wave at Someone</p>
                <p className="text-sm text-slate-400">Send a wave to show you&apos;re interested</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-pink-400 font-bold">4</span>
              </div>
              <div>
                <p className="font-medium text-white">Match!</p>
                <p className="text-sm text-slate-400">If they wave back, it&apos;s a match! Go say hi!</p>
              </div>
            </div>
          </div>

          <Button
            onClick={() => navigate('/home')}
            className="w-full mt-6 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Find Bars Near Me
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default Matchmaking;
